import React, {useEffect, useRef, useState, useMemo} from "react";
import {observer} from "mobx-react-lite";
import {useStore} from "hooks/useStore";
import useIsFocused from "hooks/useIsFocused";
import {useTranslation} from "react-i18next";
import {InfoCircleOutlined} from "@ant-design/icons";
import {Tooltip} from "antd";
import {useHistory} from "react-router-dom";
import {useFetch} from "hooks/useFetch";
import {TWENTY_SECONDS_IN_MS} from "constants/constants";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import CourierPopup from "./CourierPopup";
import {CourierIcon} from "components/_defaultComponents/CourierIcon/CourierIcon";
import {courierStatusColors} from "../../../../constants/constants";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "../../../../utils/helpers";
import {SubcompanyMarker} from "components/_defaultComponents/SubcompanyMarker/SubcompanyMarker";
import {TileLayerControl} from "components/_defaultComponents/TileLayerControl";
import {Content} from "components/dashboard/Marker/PickupMarker/Content";
import {
    MapContainer,
    InfoButton,
    InfoIcon,
    MapWrapper,
    MapDiv,
    NoCourierMessage,
    NoCourierText,
    LoadingContainer,
    LoadingText,
    ExternalPopup,
    PopupContent,
    CloseButton
} from "../style/CustomerHomeMapComponent.styles.js";

export const CustomerHomeMapComponent = observer(({selectedSubCompanyId}) => {
    const stores = useStore();
    const {i18n} = useTranslation();
    const history = useHistory();
    const [center, setCenter] = useState({lat: 41.0082, lng: 28.9784}); // İstanbul default
    const [zoom, setZoom] = useState(11);
    const [recall, setRecall] = useState(true);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [selectedSubCompanyPopup, setSelectedSubCompanyPopup] = useState(null);
    const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});
    const isFocused = useIsFocused();
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const popupRef = useRef(null);
    const tileLayerRef = useRef(null);

    const recallRef = useRef(recall);
    recallRef.current = recall;

    // Close popup when selectedSubCompanyId changes
    useEffect(() => {
        setSelectedCourier(null);
        setSelectedSubCompanyPopup(null);
    }, [selectedSubCompanyId]);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setSelectedCourier(null);
                setSelectedSubCompanyPopup(null);
            }
        };

        if (selectedCourier || selectedSubCompanyPopup) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedCourier, selectedSubCompanyPopup]);

    // Helper function to get status color
    const getStatusColor = status => {
        return courierStatusColors[status] || "#d9d9d9";
    };

    // Leaflet map initialization
    useEffect(() => {
        if (mapRef.current && !mapInstanceRef.current) {
            // Configure Leaflet marker icons using helper function
            configureLeafletMarkerIcons(L);

            // Türkiye sınırları
            const turkeyBounds = L.latLngBounds(TURKEY_BOUNDS.southWest, TURKEY_BOUNDS.northEast);

            mapInstanceRef.current = L.map(mapRef.current, {
                maxBounds: turkeyBounds,
                maxBoundsViscosity: 1.0,
                minZoom: 5,
                maxZoom: 22
            }).setView([center.lat, center.lng], zoom);

            // Add tile layer with fallback
            tileLayerRef.current = addTileLayerWithFallback(
                L,
                mapInstanceRef.current,
                getTileLayerPreference()
            );

            // Map event listeners
            mapInstanceRef.current.on("zoomstart", () => {
                // Close popups when zoom starts
                setSelectedCourier(null);
                setSelectedSubCompanyPopup(null);
            });

            mapInstanceRef.current.on("movestart", () => {
                // Close popups when map movement starts
                setSelectedCourier(null);
                setSelectedSubCompanyPopup(null);
            });
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    // Update map center and zoom when they change
    useEffect(() => {
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([center.lat, center.lng], zoom);
        }
    }, [center, zoom]);

    useEffect(() => {
        if (isFocused) {
            let timeout = null;
            timeout = setTimeout(async () => {
                await setRecall(!recallRef.current);
            }, TWENTY_SECONDS_IN_MS);
            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    const selectedSubCompany = stores.meStore.subcompanies.find(
        subCompany => subCompany._id === selectedSubCompanyId
    );
    const selectedCity = selectedSubCompany?.city || "istanbul";

    const [courierMapListLoading, courierMapListData] = useFetch({
        fetchFn: stores.courierStore.getCourierMapList.bind(null, {
            city: selectedCity,
            types: [],
            statuses: [],
            dedicatedCompanyId: "",
            dedicatedSubCompanies: [],
            dedicatedPoolIds: []
        }),
        waitForResponse: true,
        dependencies: [selectedCity, selectedSubCompanyId, recall],
        defaultDataValue: {courierStateList: []}
    });

    const [subCompanyMarkerListLoading, subCompanyMarkerList] = useFetch({
        fetchFn: stores.companyStore.getSubCompanyMapList.bind(null, {
            companyId: "",
            disabled: false,
            city: selectedCity
        }),
        initialCall: true,
        dependencies: [selectedCity],
        condition: selectedCity && selectedCity !== "all"
    });

    const courierList = useMemo(() => {
        return courierMapListData?.courierStateList || [];
    }, [courierMapListData?.courierStateList]);

    // Update markers when courier list changes
    useEffect(() => {
        if (mapInstanceRef.current) {
            // Clear existing markers
            markersRef.current.forEach(marker => {
                mapInstanceRef.current.removeLayer(marker);
            });
            markersRef.current = [];

            // Add new markers
            if (courierList && courierList.length > 0) {
                courierList.forEach((courier, index) => {
                    if (courier?.location?.coordinates) {
                        // Create custom icon with CourierIcon
                        const statusColor = getStatusColor(courier.status);
                        const iconHtml = ReactDOMServer.renderToString(
                            <div
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: statusColor,
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    border: "2px solid #fff"
                                }}
                            >
                                <CourierIcon courierType="motorCourier" />
                            </div>
                        );

                        const customIcon = L.divIcon({
                            html: iconHtml,
                            className: "custom-marker-icon courier-marker-icon",
                            iconSize: [32, 32],
                            iconAnchor: [16, 16],
                            popupAnchor: [0, -20]
                        });

                        const marker = L.marker(
                            [courier.location.coordinates[1], courier.location.coordinates[0]],
                            {icon: customIcon}
                        );

                        // Add click event to show popup outside map
                        marker.on("click", e => {
                            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                e.latlng
                            );
                            setPopupPosition({
                                x: containerPoint.x,
                                y: containerPoint.y
                            });
                            setSelectedCourier(courier);
                        });

                        marker.addTo(mapInstanceRef.current);
                        markersRef.current.push(marker);
                    }
                });
            }

            // Add subcompany markers
            if (subCompanyMarkerList && subCompanyMarkerList.length > 0) {
                subCompanyMarkerList
                    .filter(subcompany => {
                        if (selectedSubCompanyId) {
                            return subcompany._id.toString() === selectedSubCompanyId;
                        }
                        return subcompany.city === selectedCity;
                    })
                    .forEach(subcompany => {
                        // Create subcompany marker using SubcompanyMarker component
                        const subcompanyIconHtml = SubcompanyMarker({
                            subcompany,
                            isPoolCustomer: stores.meStore.isPoolCustomer
                        });

                        const subcompanyIcon = L.divIcon({
                            html: subcompanyIconHtml,
                            className: "custom-marker-icon",
                            iconSize: [36, 36],
                            iconAnchor: [18, 18],
                            popupAnchor: [0, -18]
                        });

                        const subcompanyMarker = L.marker(
                            [subcompany.location.latitude, subcompany.location.longitude],
                            {icon: subcompanyIcon}
                        );

                        // Add click event to show subcompany popup
                        subcompanyMarker.on("click", e => {
                            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                e.latlng
                            );
                            setPopupPosition({
                                x: containerPoint.x,
                                y: containerPoint.y
                            });
                            setSelectedSubCompanyPopup(subcompany);
                            setSelectedCourier(null); // Close courier popup if open
                        });

                        subcompanyMarker.addTo(mapInstanceRef.current);
                        markersRef.current.push(subcompanyMarker);
                    });
            }
        }
    }, [courierList, subCompanyMarkerList, selectedSubCompanyId, selectedCity]);

    useEffect(() => {
        if (courierList && courierList.length > 0) {
            if (courierList.length === 1) {
                const singleCourier = courierList[0];
                if (singleCourier?.location?.coordinates) {
                    setCenter({
                        lat: singleCourier.location.coordinates[1],
                        lng: singleCourier.location.coordinates[0]
                    });
                    setZoom(13);
                }
            } else {
                const coordinates = courierList
                    .filter(courier => courier?.location?.coordinates)
                    .map(courier => ({
                        lat: courier.location.coordinates[1],
                        lng: courier.location.coordinates[0]
                    }));

                if (coordinates.length > 0) {
                    const bounds = coordinates.reduce(
                        (bounds, coord) => {
                            bounds.minLat = Math.min(bounds.minLat, coord.lat);
                            bounds.maxLat = Math.max(bounds.maxLat, coord.lat);
                            bounds.minLng = Math.min(bounds.minLng, coord.lng);
                            bounds.maxLng = Math.max(bounds.maxLng, coord.lng);
                            return bounds;
                        },
                        {
                            minLat: coordinates[0].lat,
                            maxLat: coordinates[0].lat,
                            minLng: coordinates[0].lng,
                            maxLng: coordinates[0].lng
                        }
                    );

                    // Merkez noktasını hesapla
                    const centerLat = (bounds.minLat + bounds.maxLat) / 2;
                    const centerLng = (bounds.minLng + bounds.maxLng) / 2;

                    setCenter({
                        lat: centerLat,
                        lng: centerLng
                    });

                    // Zoom seviyesini kurye sayısına göre ayarla
                    const latDiff = bounds.maxLat - bounds.minLat;
                    const lngDiff = bounds.maxLng - bounds.minLng;
                    const maxDiff = Math.max(latDiff, lngDiff);

                    let newZoom;
                    if (maxDiff < 0.01) {
                        newZoom = 14; // Yakın kuryeler - daha zoom in
                    } else if (maxDiff < 0.05) {
                        newZoom = 12; // Orta mesafe
                    } else if (maxDiff < 0.1) {
                        newZoom = 10; // Orta-uzak mesafe
                    } else {
                        newZoom = 8; // Uzak kuryeler - daha zoom out
                    }

                    setZoom(newZoom);
                }
            }
        } else if (selectedSubCompany && selectedSubCompany.location) {
            // Kurye yoksa alt firma konumuna odaklan
            setCenter({
                lat: selectedSubCompany.location.latitude,
                lng: selectedSubCompany.location.longitude
            });
            setZoom(13);
        } else if (subCompanyMarkerList && subCompanyMarkerList.length > 0) {
            // Eğer selectedSubCompany yoksa ama subCompanyMarkerList varsa, ilk alt firmaya odaklan
            const firstSubCompany = subCompanyMarkerList.find(
                subcompany => subcompany.city === selectedCity
            );
            if (firstSubCompany && firstSubCompany.location) {
                setCenter({
                    lat: firstSubCompany.location.latitude,
                    lng: firstSubCompany.location.longitude
                });
                setZoom(13);
            }
        }
    }, [courierMapListData, selectedSubCompany, subCompanyMarkerList, selectedCity]);

    const handleGoToLiveMap = () => {
        history.push("/musteri/harita");
    };

    return (
        <MapContainer>
            <InfoButton>
                <Tooltip title="Canlı haritada gör" placement="left">
                    <InfoIcon>
                        <InfoCircleOutlined onClick={handleGoToLiveMap} />
                    </InfoIcon>
                </Tooltip>
            </InfoButton>

            {selectedSubCompanyId ? (
                <MapWrapper>
                    <MapDiv ref={mapRef} />
                    {/* Tile Layer Control */}
                    <TileLayerControl
                        mapInstance={mapInstanceRef.current}
                        tileLayerRef={tileLayerRef}
                        style={{top: "10px", right: "10px"}}
                    />
                </MapWrapper>
            ) : (
                <LoadingContainer>
                    <LoadingText>Harita yükleniyor...</LoadingText>
                </LoadingContainer>
            )}

            {/* External popup */}
            {selectedCourier && (
                <ExternalPopup ref={popupRef} x={popupPosition.x} y={popupPosition.y}>
                    <PopupContent>
                        <CloseButton onClick={() => setSelectedCourier(null)}>×</CloseButton>
                        <CourierPopup courier={selectedCourier} />
                    </PopupContent>
                </ExternalPopup>
            )}

            {/* SubCompany popup */}
            {selectedSubCompanyPopup && (
                <ExternalPopup ref={popupRef} x={popupPosition.x} y={popupPosition.y}>
                    <PopupContent>
                        <CloseButton onClick={() => setSelectedSubCompanyPopup(null)}>
                            ×
                        </CloseButton>
                        <Content pickupInfo={selectedSubCompanyPopup} />
                    </PopupContent>
                </ExternalPopup>
            )}
        </MapContainer>
    );
});
